import React, { useState, useCallback } from 'react';
import { getWordOfTheDay } from '../data/wordOfTheDay';

/*
 * Homepage "Word of the Day" card. The word is chosen from the current date, so
 * it changes every day (a freshness + return-visit signal) while still
 * prerendering to real, content-rich HTML at build time.
 *
 * The Share button pushes the word + meaning + example + a compelling invite
 * back to wordzingo.com — native share sheet on mobile, clipboard on desktop.
 */

const SHARE_URL = 'https://wordzingo.com';
// Keep the message tweet-friendly and safe for SMS concatenation.
const SHARE_LIMIT = 280;

// Build the shareable message, capped to SHARE_LIMIT. The word, example, and
// the call-to-action always survive; only the definition is trimmed when a
// day's entry runs long (and the example is dropped only as a last resort).
export function buildShareText(w) {
  const caption = `\n\n🧩 Love word puzzles? Try 👉 ${SHARE_URL}`;
  const header = `📖 Word of the Day: ${w.word.toUpperCase()} (${w.pos})`;
  const example = w.example ? `\n📝 “${w.example}”` : '';
  const fullMeaning = `\n💡 ${w.definition}`;

  const full = header + fullMeaning + example + caption;
  if (full.length <= SHARE_LIMIT) return full;

  // 1) Keep the example — trim the definition to make room.
  const room = SHARE_LIMIT - header.length - example.length - caption.length - 5; // "\n💡 " + "…"
  if (room > 16) {
    return header + `\n💡 ${w.definition.slice(0, room).trim()}…` + example + caption;
  }

  // 2) Example itself too long — drop it, keep the meaning.
  const noExample = header + fullMeaning + caption;
  if (noExample.length <= SHARE_LIMIT) return noExample;
  const room2 = SHARE_LIMIT - header.length - caption.length - 5;
  const trimmed = room2 > 12 ? `\n💡 ${w.definition.slice(0, room2).trim()}…` : '';
  return header + trimmed + caption;
}

export default function WordOfTheDay() {
  const today = new Date();
  const w = getWordOfTheDay(today);
  const [copied, setCopied] = useState(false);

  const dateLabel = today.toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  const handleShare = useCallback(async () => {
    const text = buildShareText(w);
    // Native share sheet (mobile + some desktops).
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: 'Word of the Day · WordZingo', text });
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') return; // user dismissed the sheet
        // any other error → fall through to clipboard
      }
    }
    // Clipboard fallback (desktop).
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (_) {
      /* clipboard blocked — nothing more we can safely do */
    }
  }, [w]);

  return (
    <section className="container-xl py-3" style={{ maxWidth: 720 }}>
      <div
        style={{
          background: 'linear-gradient(135deg, #7c3aed 0%, #db2777 100%)',
          color: '#fff',
          borderRadius: 16,
          padding: '1rem 1.25rem',
          boxShadow: '0 6px 18px rgba(124,58,237,0.22)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.72rem', opacity: 0.9 }}>
            🗓 Word of the Day · {dateLabel}
          </div>
          <button
            type="button"
            onClick={handleShare}
            aria-label="Share the Word of the Day"
            style={{
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              background: 'rgba(255,255,255,0.18)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.35)',
              borderRadius: 999,
              padding: '0.3rem 0.8rem',
              fontSize: '0.8rem',
              fontWeight: 800,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'background 0.15s ease',
            }}
            onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }}
            onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; }}
          >
            {copied ? '✓ Copied!' : '🔗 Share'}
          </button>
        </div>

        <h2 style={{ fontWeight: 900, fontSize: '1.6rem', margin: '0.25rem 0 0.1rem' }}>
          {w.word}
          <span style={{ fontWeight: 400, fontStyle: 'italic', fontSize: '0.85rem', opacity: 0.9, marginLeft: '0.6rem' }}>
            {w.pron} · {w.pos}
          </span>
        </h2>

        <p style={{ fontSize: '0.98rem', lineHeight: 1.5, margin: '0.35rem 0 0.4rem' }}>{w.definition}</p>
        {w.example && (
          <p style={{ fontSize: '0.9rem', lineHeight: 1.5, margin: '0 0 0.4rem', fontStyle: 'italic', opacity: 0.95 }}>
            📝 “{w.example}”
          </p>
        )}
        <p style={{ fontSize: '0.9rem', lineHeight: 1.5, margin: 0, opacity: 0.95 }}>💡 {w.fact}</p>
      </div>
    </section>
  );
}
